<template>
	<div class="cars-filter">
		<v-menu location="bottom left" offset="12" scroll-strategy="close" content-class="cars-filter__filter-menu" :close-on-content-click="false">
			<template #activator="{ props, isActive }">
				<v-btn
					v-if="isMobile_lg && !filter.isSort"
					:disabled="filter.disabled"
					v-bind="props"
					rounded
					flat
					class="cars-filter__activator"
					@click.stop.capture="isFiltersModalOpen = true">
					{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}
					<span v-if="filter.isSort && carsStore.getCurrentSorting">
						: {{ i18nService.$tDynamic('modules.cars_module.cars.filters', carsStore.getCurrentSorting.value) }}
					</span>
					<template #append>
						<div class="cars-filter__append">
							<div v-if="selectedValues.length" class="cars-filter__append-count-selected">
								{{ selectedValues.length }}
							</div>
							<div v-if="selectedSeats && selectedSeats !== 1" class="cars-filter__append-count-selected">1</div>
							<div v-if="selectedSeats && selectedSeats !== 1" class="cars-filter__append-count-selected">1</div>
							<div v-if="selectedGroups?.length" class="cars-filter__append-count-selected">
								{{ selectedGroups.length }}
							</div>
							<div v-if="filter.filterName === EFilterNames.PRICE && selectedPriceRange" class="cars-filter__append-count-selected">
								1
							</div>
							<div
								v-if="filter.isAll && (carsStore.getCurrentFilters?.length || carsStore.getCurrentGroups?.length)"
								class="cars-filter__append-count-selected">
								<span
									v-if="
										(currentFiltersSelectedCount || currentFiltersSelectedCount === 0) &&
										(carsStore.getCurrentGroups?.length || carsStore.getCurrentGroups?.length === 0)
									">
									{{ currentFiltersSelectedCount + carsStore.getCurrentGroups?.length }}
								</span>
							</div>
							<GlobalIcon
								class="cars-filter__append-icon"
								:class="{ 'cars-filter__append-icon--active': isActive }"
								:height="14"
								name="arrow-down" />
						</div>
					</template>
				</v-btn>
				<v-btn v-else v-bind="props" :disabled="filter.disabled" rounded flat class="cars-filter__activator">
					{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}
					<span v-if="filter.isSort && carsStore.getCurrentSorting">
						: {{ i18nService.$tDynamic('modules.cars_module.cars.filters', carsStore.getCurrentSorting.value) }}
					</span>
					<template #append>
						<div class="cars-filter__append">
							<div v-if="selectedValues.length && !filter.isSeats" class="cars-filter__append-count-selected">
								{{ selectedValues.length }}
							</div>
							<div v-if="selectedSeats && selectedSeats !== 1" class="cars-filter__append-count-selected">1</div>
							<div
								v-if="selectedGroups?.length && !filter.isSeats && filter.filterName === EFilterNames.GROUP"
								class="cars-filter__append-count-selected">
								{{ selectedGroups.length }}
							</div>
							<div v-if="filter.filterName === EFilterNames.PRICE && selectedPriceRange" class="cars-filter__append-count-selected">
								1
							</div>

							<GlobalIcon
								class="cars-filter__append-icon"
								:class="{ 'cars-filter__append-icon--active': isActive }"
								:height="14"
								name="arrow-down" />
						</div>
					</template>
				</v-btn>
			</template>

			<v-list v-if="filter.filterName === EFilterNames.GROUP && allGroups">
				<v-list-item v-for="(item, i) in allGroups" :key="i">
					<GlobalCheckbox v-model="selectedGroups" readonly :value="item.name" @click="carsStore.setCurrentGroup(item)">
						{{
							typeof item.name === 'string'
								? i18nService.$tDynamic('data.group_map_translations', groupMapTranslations[item.name])
								: item.name
						}}
					</GlobalCheckbox>
				</v-list-item>
			</v-list>

			<v-list v-if="!filter.isSeats && parameter?.values">
				<v-list-item v-for="(item, i) in parameter.values" :key="i">
					<GlobalCheckbox
						v-if="parameter"
						v-model="selectedValues"
						readonly
						:value="item"
						@click="carsStore.setCurrentFilterByValue(parameter, item)"
						>{{ typeof item === 'string' ? i18nService.$tDynamic('data.group_map_translations', parametersMapTranslations[item]) : item }}
					</GlobalCheckbox>
				</v-list-item>
			</v-list>
			<v-list v-else-if="filter.filterName === EFilterNames.PRICE">
				<v-radio-group v-model="selectedPriceRange" hide-details>
					<GlobalRadio
						v-for="(item, i) in priceRanges"
						:key="i"
						class="cars-filter__filter-menu-radio"
						readonly
						density="compact"
						color="var(--primary-color)"
						:label="item.name"
						:value="item"
						@click="carsStore.setCurrentPriceRange(item)" />
				</v-radio-group>
			</v-list>

			<v-list v-else-if="filter.isSort">
				<v-radio-group v-model="carsStore.getCurrentSorting" hide-details>
					<GlobalRadio
						v-for="(item, i) in filter.items"
						:key="i"
						class="cars-filter__filter-menu-radio"
						readonly
						density="compact"
						color="var(--primary-color)"
						:label="i18nService.$tDynamic('modules.cars_module.cars.filters', item.name)"
						:value="item"
						@click="carsStore.setCurrentSorting(item as ICarSorting)" />
				</v-radio-group>
			</v-list>
			<v-list v-else-if="filter.isSeats">
				<v-list-item>
					<v-slider
						v-model="selectedSeats"
						class="cars-filter__filter-menu-slider"
						:ticks="seatsLabels"
						:step="1"
						hide-details
						show-ticks="always"
						tick-size="0"
						thumb-color="var(--primary-color)"
						track-fill-color="var(--primary-color)"
						track-color="var(--stroke-on-background)"
						:track-size="4"
						:min="1"
						:thumb-size="16"
						:max="5" />
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script setup lang="ts">
import { EFilterNames, type ICarSorting, type IPriceRange } from '@/modules/carsModule/types/filters'

const { isMobile_lg } = useWindowWidth()
const isFiltersModalOpen = inject<boolean>('isFiltersModalOpen')!
const carsStore = useCarsStore()
const selectedValues = ref<string[] | number[]>([])
const selectedGroups = ref<string[]>()
const selectedSeats = ref<number>()
const selectedPriceRange = ref<IPriceRange>()
const i18nService = usei18nService()

export interface ISingleFilterProps {
	title: string
	filterName?: EFilterNames
	items?: {
		name: string
		value: string
	}[]
	disabled?: boolean
	isSort?: boolean
	isSeats?: boolean
	isAll?: boolean
}

const componentProps = defineProps<{
	filter: ISingleFilterProps
}>()

const parameter = computed(() => {
	if (componentProps.filter.filterName) {
		return carsStore.getFilterByParameterName(componentProps.filter.filterName)
	} else {
		return undefined
	}
})

const currentFiltersSelectedCount = computed(() => {
	const minMaxFilters = carsStore.getCurrentFilters?.filter((filter) => filter.max || filter.min).flat()

	const standardFilters = carsStore.getCurrentFilters?.map((filter) => filter.values).flat()

	return (minMaxFilters?.length ?? 0) + (standardFilters?.length ?? 0)
})

const allGroups = computed(() => carsStore.getAllGroups?.filter((group) => group.name !== 'electric' && group.name !== 'automatic'))

const seatsLabels = {
	1: 'Dow.',
	2: '2+',
	3: '4+',
	4: '5+',
	5: '7+',
}

const priceRanges: IPriceRange[] = [
	{
		name: '0 zł - 100 zł',
		value: '0-100',
		min: 0,
		max: 10000,
	},
	{
		name: '101 zł - 200 zł',
		value: '100-200',
		min: 10100,
		max: 20000,
	},
	{
		name: '201 zł - 300 zł',
		value: '200-300',
		min: 20100,
		max: 30000,
	},
	{
		name: '301 zł +',
		value: '300+',
		min: 30100,
	},
]

watch(
	() => selectedSeats.value,
	(val) => {
		if (parameter.value) {
			switch (val) {
				case 1:
					carsStore.setCurrentFilter(parameter.value, undefined, undefined)
					break
				case 2:
					carsStore.setCurrentFilter(parameter.value, undefined, 2)
					break
				case 3:
					carsStore.setCurrentFilter(parameter.value, undefined, 4)
					break
				case 4:
					carsStore.setCurrentFilter(parameter.value, undefined, 5)
					break
				case 5:
					carsStore.setCurrentFilter(parameter.value, undefined, 7)
					break
			}
		}
	},
	{ deep: true, immediate: true },
)

watch(
	() => carsStore.getCurrentPriceRange,
	(currentPriceRange) => {
		selectedPriceRange.value = currentPriceRange as IPriceRange
	},
	{ deep: true, immediate: true },
)

watch(
	() => carsStore.getCurrentFilters,
	(currentFilters) => {
		if (parameter.value) {
			const currentFilter = carsStore.getCurrentFilterById(parameter.value.id)

			selectedValues.value = currentFilter?.values ? currentFilter.values : []

			if (componentProps.filter.isSeats && !currentFilters?.find((filter) => filter.parameter?.name === 'Seats')) {
				selectedSeats.value = undefined
			}
		}
	},
	{ deep: true, immediate: true },
)

watch(
	() => carsStore.getCurrentGroups,
	(currentGroups) => {
		if (componentProps.filter.filterName === EFilterNames.GROUP) {
			selectedGroups.value = currentGroups?.map((group) => group.name)
		}
	},
	{ deep: true, immediate: true },
)
</script>

<style lang="scss">
@import './SingleFilter';
</style>
